import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Package, LogOut, UserRound, KeyRound, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/lib/seo";
import { useCustomer } from "@/lib/customer";
import { formatPrice, formatDate, statusLabel, DAKAR_QUARTIERS } from "@/lib/format";
import { INPUT } from "./Connexion";

const STATUS_COLOR = {
  en_attente: "bg-tan/40 text-ink",
  confirmee: "bg-mint/40 text-teal-dark",
  en_livraison: "bg-teal/15 text-teal-dark",
  livree: "bg-teal text-cream",
  annulee: "bg-[#b04a4a]/10 text-[#b04a4a]",
};

function OrderCard({ o }) {
  const [open, setOpen] = useState(false);
  return (
    <article data-testid={`my-order-${o.ref}`} className="rounded-3xl border border-line bg-white overflow-hidden">
      <button type="button" data-testid={`my-order-toggle-${o.ref}`} onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-4 text-left">
        <div className="flex items-center gap-4 min-w-0">
          <span className={`rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-wider whitespace-nowrap ${STATUS_COLOR[o.status] || ""}`}>{statusLabel(o.status)}</span>
          <div className="min-w-0">
            <p className="font-mono font-medium text-teal">{o.ref}</p>
            <p className="text-xs text-mauve">{formatDate(o.created_at)} — {o.items?.length} article{o.items?.length > 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <p className="font-semibold text-teal">{formatPrice(o.total)}</p>
          <ChevronDown size={18} className={`text-mauve transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-line px-5 md:px-7 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <ul className="space-y-1.5 text-sm">
            {o.items?.map((it, i) => (
              <li key={i} className="flex justify-between gap-4 text-ink/85">
                <span>{it.name}{it.variant && <span className="ml-1.5 rounded-full bg-mint/30 px-2 py-0.5 text-[11px] text-teal-dark">{it.variant}</span>} × {it.qty}</span>
                <span>{formatPrice(it.price * it.qty)}</span>
              </li>
            ))}
            {o.discount > 0 && <li className="flex justify-between text-[#b04a4a]"><span>Remise {o.promo_code}</span><span>- {formatPrice(o.discount)}</span></li>}
          </ul>
          <div className="text-sm text-ink/80">
            <p className="eyebrow mb-2">Livraison</p>
            <p>{o.customer_address}{o.customer_zone ? ` — ${o.customer_zone}` : ""}</p>
            <p className="mt-3 text-xs font-mono uppercase tracking-wider text-mauve">Paiement à la livraison</p>
          </div>
        </div>
      )}
    </article>
  );
}

function ProfileForm({ customer, setCustomer }) {
  const [form, setForm] = useState({ name: customer.name || "", phone: customer.phone || "", zone: customer.zone || "", address: customer.address || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/customer/me", form);
      setCustomer(data);
      toast.success(data.linked_orders ? `Informations enregistrées — ${data.linked_orders} commande(s) rattachée(s)` : "Informations enregistrées");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} data-testid="customer-profile-form" className="space-y-4">
      <label className="block text-sm font-medium text-ink">Nom complet
        <input required data-testid="profile-name-input" value={form.name} onChange={set("name")} className={INPUT} />
      </label>
      <label className="block text-sm font-medium text-ink">Téléphone
        <input type="tel" data-testid="profile-phone-input" value={form.phone} onChange={set("phone")} placeholder="+221 77 123 45 67" className={INPUT} />
      </label>
      <label className="block text-sm font-medium text-ink">Quartier
        <select data-testid="profile-zone-input" value={form.zone} onChange={set("zone")} className={INPUT}>
          <option value="">Choisissez…</option>
          {DAKAR_QUARTIERS.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </label>
      <label className="block text-sm font-medium text-ink">Adresse de livraison
        <input data-testid="profile-address-input" value={form.address} onChange={set("address")} placeholder="Rue, immeuble, repère…" className={INPUT} />
      </label>
      <button type="submit" data-testid="profile-save-btn" disabled={saving} className="rounded-full bg-teal px-7 py-3 text-sm font-medium text-cream hover:bg-teal-dark transition-colors disabled:opacity-60 flex items-center gap-2">
        {saving && <Loader2 size={15} className="animate-spin" />} Enregistrer
      </button>
    </form>
  );
}

function PasswordForm({ hasPassword }) {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/customer/password", form);
      toast.success(hasPassword ? "Mot de passe modifié" : "Mot de passe défini — vous pouvez aussi vous connecter par email");
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} data-testid="customer-password-form" className="space-y-4">
      {hasPassword && (
        <label className="block text-sm font-medium text-ink">Mot de passe actuel
          <input required type="password" autoComplete="current-password" data-testid="password-current-input" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={INPUT} />
        </label>
      )}
      <label className="block text-sm font-medium text-ink">{hasPassword ? "Nouveau mot de passe" : "Choisir un mot de passe"}
        <input required type="password" autoComplete="new-password" data-testid="password-new-input" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={INPUT} />
        <span className="mt-1 block text-xs text-ink/60 font-normal">Au moins 8 caractères.</span>
      </label>
      <button type="submit" data-testid="password-save-btn" disabled={saving} className="rounded-full border border-teal px-7 py-3 text-sm font-medium text-teal hover:bg-teal hover:text-cream transition-colors disabled:opacity-60">
        {hasPassword ? "Modifier" : "Définir le mot de passe"}
      </button>
    </form>
  );
}

export default function MonCompte() {
  const { customer, setCustomer, logout } = useCustomer();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (customer === null) navigate("/compte/connexion", { replace: true });
  }, [customer, navigate]);

  useEffect(() => {
    if (!customer) return;
    api.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [customer]);

  if (!customer) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 size={32} className="animate-spin text-teal" /></div>;
  }

  const doLogout = async () => {
    await logout();
    toast.success("À bientôt !");
    navigate("/");
  };

  const TABS = [
    { id: "orders", label: "Mes commandes", icon: Package, testid: "account-tab-orders" },
    { id: "profile", label: "Mes informations", icon: UserRound, testid: "account-tab-profile" },
    { id: "password", label: "Mot de passe", icon: KeyRound, testid: "account-tab-password" },
  ];

  return (
    <div data-testid="customer-account-page" className="min-h-[85vh] bg-cream-light pt-32 md:pt-40 pb-20">
      <SEO title="Mon compte" description="Votre espace cliente Tendres Tétées : commandes et informations de livraison." />
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow mb-3">Espace cliente</p>
            <h1 className="font-serif text-3xl md:text-5xl text-teal-dark">Bonjour {customer.name?.split(" ")[0] || ""} <span className="text-teal italic">!</span></h1>
            <p data-testid="account-email" className="mt-2 text-sm text-ink/70">{customer.email}</p>
          </div>
          <button type="button" data-testid="customer-logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm text-ink hover:text-[#b04a4a] transition-colors">
            <LogOut size={15} /> Me déconnecter
          </button>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto">
              {TABS.map((tb) => {
                const Icon = tb.icon;
                const active = tab === tb.id;
                return (
                  <button key={tb.id} type="button" data-testid={tb.testid} onClick={() => setTab(tb.id)} className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-sm whitespace-nowrap transition-colors ${active ? "bg-teal text-cream" : "text-ink hover:bg-white"}`}>
                    <Icon size={17} /> {tb.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          <section className="lg:col-span-9">
            {tab === "orders" && (
              <div className="space-y-3">
                {orders === null && <div className="h-40 rounded-3xl bg-white animate-pulse" />}
                {orders?.length === 0 && (
                  <div data-testid="my-orders-empty" className="rounded-3xl border border-line bg-white p-10 text-center">
                    <p className="font-serif text-2xl text-teal-dark">Aucune commande pour l'instant</p>
                    <p className="mt-2 text-sm text-ink/70">Vos prochaines commandes apparaîtront ici. Si vous avez déjà commandé par le passé, ajoutez votre numéro de téléphone dans « Mes informations » pour les retrouver.</p>
                    <Link to="/boutique" data-testid="my-orders-empty-cta" className="mt-6 inline-block rounded-full bg-teal px-8 py-3 text-sm font-medium text-cream hover:bg-teal-dark">Découvrir la boutique</Link>
                  </div>
                )}
                {orders?.map((o) => <OrderCard key={o.id} o={o} />)}
              </div>
            )}
            {tab === "profile" && (
              <div className="rounded-3xl border border-line bg-white p-6 md:p-8 max-w-xl">
                <h2 className="font-serif text-2xl text-teal-dark mb-6">Mes informations</h2>
                <ProfileForm customer={customer} setCustomer={setCustomer} />
              </div>
            )}
            {tab === "password" && (
              <div className="rounded-3xl border border-line bg-white p-6 md:p-8 max-w-xl">
                <h2 className="font-serif text-2xl text-teal-dark mb-2">Mot de passe</h2>
                {customer.google && !customer.has_password && <p className="mb-6 text-sm text-ink/70">Vous vous connectez avec Google. Vous pouvez aussi définir un mot de passe pour vous connecter par email.</p>}
                <PasswordForm hasPassword={customer.has_password} />
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
